import { Component, inject, OnInit } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ActivatedRoute, Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { finalize, retry } from "rxjs";
import { environment } from "../../../../environments/environment";
import { JwtService } from "../../services/auth/jwt.service";
import { ErrorHandlerService } from "../../services/error-handler/error-handler.service";
import { FormsModule } from "@angular/forms";
import { FloatLabel } from "primeng/floatlabel";
import { InputText } from "primeng/inputtext";
import { Password } from "primeng/password";
import { Message } from "primeng/message";
import { ProgressBar } from "primeng/progressbar";
import { Button } from "primeng/button";
import { LoginService } from "./service/login.service";
import { ResponseInfo } from "../../services/auth/model/response-info.model";
import { LoginPageComponent } from "./login-page/login-page.component";

@Component({
	selector: "app-login",
	templateUrl: "./login.component.html",
	imports: [
		FormsModule,
		FloatLabel,
		InputText,
		Password,
		Message,
		ProgressBar,
		Button,
		LoginPageComponent
	]
})
export class LoginComponent implements OnInit {
	private readonly jwtService = inject(JwtService);
	private readonly errorHandlerService = inject(ErrorHandlerService);

	private readonly messageService = inject(MessageService);
	private readonly loginService = inject(LoginService);
	private readonly http = inject(HttpClient);
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);

	username: string = "";
	password: string = "";
	loginInProgress: boolean = false;
	passwordVisible: boolean = false;
	notAuthorized: boolean = false;

	private returnUrl: string = "/";
	private readonly loginURL = environment.apiGatewayUrl + "/public/auth/login";

	ngOnInit() {
		this.notAuthorized = this.route.snapshot.queryParamMap.get("error") === "not_authorized";
		this.returnUrl = this.route.snapshot.queryParamMap.get("returnUrl") || "/";
		this.username = localStorage.getItem("username") || this.username;

		if (this.returnUrl) sessionStorage.setItem("returnUrl", this.returnUrl);
	}

	continue() {
		if (!this.username) return;

		localStorage.setItem("username", this.username);
		this.loginInProgress = true;
		this.passwordVisible = false;

		this.loginService
			.discover(this.username)
			.pipe(finalize(() => (this.loginInProgress = false)))
			.subscribe({
				next: ({ method, redirectUrl }) => {
					switch (method) {
						case "SSO":
							this.loginWithSso();
							break;
						case "EXTERNAL":
							if (redirectUrl) this.loginWithRedirectUrl(redirectUrl);
							else this.discoveryFailed();
							break;
						case "PASSWORD":
							this.loginInProgress = false;
							this.passwordVisible = true;
							break;
						default:
							this.discoveryFailed();
					}
				}
			});
	}

	login() {
		this.loginInProgress = true;

		this.http
			.post<ResponseInfo>(this.loginURL, {
				username: this.username,
				password: this.password
			})
			.pipe(
				retry(3),
				finalize(() => {
					sessionStorage.removeItem("returnUrl");
					this.loginInProgress = false;
				})
			)
			.subscribe({
				next: result => {
					this.jwtService.setTokensFromResponse(result);
					void this.router.navigateByUrl(this.returnUrl);
				},
				error: error =>
					this.errorHandlerService.showError(error, "Failed to login! Invalid user or password")
			});
	}

	onUsernameChange() {
		this.passwordVisible = false;
		this.password = "";
	}

	private loginWithSso() {
		globalThis.location.assign(environment.cognitoSsoLoginUrl);
	}

	private loginWithRedirectUrl(redirectUrl: string) {
		globalThis.location.assign(redirectUrl);
	}

	private discoveryFailed() {
		this.loginInProgress = false;
		this.messageService.add({
			severity: "error",
			summary: "Error",
			detail: "Unsupported login method"
		});
	}
}
